import { z } from 'zod';
import { TimestampSchema } from './common.js';
import { UserRefSchema } from './domain.js';
/**
 * Ссылки направления: адрес и наименование к нему. Второй после заметок вид содержимого,
 * которое живёт только в Jisy, — макеты, таблицы, доски, стенды.
 *
 * Адрес разбирается схемой, а не берётся строкой как есть: он уходит в `href`, и `javascript:`
 * оттуда — выполнение чужого кода на странице. Разрешены только `http` и `https`.
 */
export const LINK_TITLE_MAX = 200;
export const LINK_URL_MAX = 2000;
/**
 * Адрес ссылки. `URL` разбирает строку теми же правилами, что и браузер, — на них и опираемся,
 * добавляя запрет на все схемы, кроме сетевых. Адрес без схемы («jira.example.com») дополняется
 * до `https://`: люди копируют его из адресной строки, где схемы не видно.
 */
export const LinkUrlSchema = z
    .string()
    .trim()
    .min(1)
    .max(LINK_URL_MAX)
    .transform((value) => (/^[a-z][a-z0-9+.-]*:/iu.test(value) ? value : `https://${value}`))
    .refine((value) => {
    try {
        const url = new URL(value);
        // Пустой хост — это «https://», «http:///path» и подобное: открывать там нечего.
        return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.length > 0;
    }
    catch {
        return false;
    }
}, 'ссылка должна быть адресом http или https');
export const ProjectLinkSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    /** Наименование. Пустое допустимо — в списке тогда виден сам адрес. */
    title: z.string(),
    url: z.string(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    updatedBy: UserRefSchema.nullable(),
});
export const ProjectLinkListSchema = z.object({ links: z.array(ProjectLinkSchema) });
export const ProjectLinkCreateSchema = z.object({
    url: LinkUrlSchema,
    title: z.string().trim().max(LINK_TITLE_MAX).default(''),
});
/** Правка ссылки: наименование и адрес меняются порознь, каждое своим полем строки. */
export const ProjectLinkPatchSchema = z
    .object({
    url: LinkUrlSchema.optional(),
    title: z.string().trim().max(LINK_TITLE_MAX).optional(),
})
    .refine((value) => value.title !== undefined || value.url !== undefined, 'пустая правка');
/**
 * Подпись ссылки в списке: наименование, а без него — адрес без схемы и «www».
 * Живёт в общем пакете, чтобы сервер и клиент называли ссылку одинаково.
 */
export function linkLabel(link) {
    const title = link.title.trim();
    if (title.length > 0)
        return title;
    try {
        const url = new URL(link.url);
        const path = url.pathname === '/' ? '' : url.pathname;
        return `${url.hostname.replace(/^www\./u, '')}${path}${url.search}`;
    }
    catch {
        return link.url;
    }
}
//# sourceMappingURL=links.js.map